// features/pos/components/CustomerModal.tsx
// ENHANCED - Better UX, Quick Actions, Customer Insights, Recent Customers

import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '@/store/authStore';
import { usePosStore } from '@/store/posStore';
import { motion, AnimatePresence } from 'framer-motion';
import {
    User, Phone, Search, Plus, X, Star, CreditCard,
    ShieldCheck, Loader2, UserPlus, Users, History,
    Award, TrendingUp, Clock, ArrowRight, Gift, Calendar
} from 'lucide-react';
import { clsx } from 'clsx';
import apiClient from '@/lib/api-client';

interface CustomerInfo {
    id: string; name: string; phone: string;
    loyaltyCardNumber: string; loyaltyPoints: number;
    loyaltyTier: string; storeCredit: number;
    tierDiscountPercent: number;
}

interface CustomerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSearch: (phone: string) => Promise<any>;
    onCreateCustomer: (form: { firstName: string; lastName: string; phone: string; dateOfBirth?: string }) => Promise<any>;
    currentCustomer: CustomerInfo | null;
    onRemoveCustomer: () => void;
}

const RECENT_CUSTOMERS_KEY = 'pos-recent-customers';
const MAX_RECENT = 5;

export function CustomerModal({ isOpen, onClose, onSearch, onCreateCustomer, currentCustomer, onRemoveCustomer }: CustomerModalProps) {
    const user = useAuthStore((s) => s.user);
    const pos = usePosStore();
    const tenantId = user?.tenantId || '';

    const [mode, setMode] = useState<'search' | 'create'>('search');
    const [phone, setPhone] = useState('');
    const [form, setForm] = useState({ firstName: '', lastName: '', phone: '', dateOfBirth: '' });
    const [isLoading, setIsLoading] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [verificationRef, setVerificationRef] = useState<string | null>(null);
    const [recentCustomers, setRecentCustomers] = useState<any[]>([]);
    const [showRecent, setShowRecent] = useState(true);

    // Live search
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Load recent customers
    useEffect(() => {
        if (isOpen) {
            try {
                const saved = localStorage.getItem(RECENT_CUSTOMERS_KEY);
                if (saved) {
                    const recent = JSON.parse(saved);
                    if (Array.isArray(recent)) {
                        setRecentCustomers(recent.slice(0, MAX_RECENT));
                    }
                }
            } catch { }
        }
    }, [isOpen]);

    // Save to recent on successful attachment
    const saveToRecent = (customer: any) => {
        const customerData = {
            id: customer.id,
            firstName: customer.firstName || customer.name?.split(' ')[0] || '',
            lastName: customer.lastName || customer.name?.split(' ').slice(1).join(' ') || '',
            phone: customer.phone || '',
            loyaltyTier: customer.loyaltyTier || 'bronze',
            loyaltyPoints: customer.loyaltyPoints || 0,
        };

        const updated = [customerData, ...recentCustomers.filter(c => c.id !== customerData.id)]
            .slice(0, MAX_RECENT);
        setRecentCustomers(updated);
        localStorage.setItem(RECENT_CUSTOMERS_KEY, JSON.stringify(updated));
    };

    // Focus input on open
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 200);
        }
    }, [isOpen, mode]);

    // Reset on close
    useEffect(() => {
        if (!isOpen) {
            setPhone('');
            setSearchResults([]);
            setMode('search');
            setForm({ firstName: '', lastName: '', phone: '', dateOfBirth: '' });
            setShowRecent(true);
        }
    }, [isOpen]);

    // Live search with fast debounce (starts from 1 char)
    const performSearch = useCallback(async (query: string) => {
        if (!query.trim()) {
            setSearchResults([]);
            return;
        }

        setIsSearching(true);
        try {
            const currentBranchId = useAuthStore.getState().selectedBranchId || (user as any)?.branchId;
            const { data } = await apiClient.get(
                `/tenants/${tenantId}/customers`,
                { params: { search: query.trim(), limit: 12, branchId: currentBranchId || undefined } }
            );
            const results = data?.data || data || [];
            setSearchResults(Array.isArray(results) ? results : []);
        } catch {
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    }, [tenantId]);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (phone.trim().length >= 1) {
            setShowRecent(false);
            debounceRef.current = setTimeout(() => {
                performSearch(phone);
            }, 80);
        } else {
            setSearchResults([]);
            setIsSearching(false);
            setShowRecent(true);
        }

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [phone, performSearch]);

    // Background verification
    const verifyCustomerInBackground = async (customerPhone: string, firstName?: string, lastName?: string) => {
        if (!customerPhone) return;
        setIsVerifying(true);
        pos.setCustomerVerification('pending');
        try {
            const { data } = await apiClient.post(`/tenants/${tenantId}/customers/verify`, {
                phone: customerPhone,
                firstName,
                lastName,
            });
            const verified = data?.data?.verified || false;
            const ref = data?.data?.verificationRef || '';
            setVerificationRef(ref);
            pos.setCustomerVerification(verified ? 'verified' : 'unverified');
            if (ref) localStorage.setItem(`verify-ref-${customerPhone}`, ref);
        } catch {
            pos.setCustomerVerification('unverified');
        } finally {
            setIsVerifying(false);
        }
    };

    const handleSelectCustomer = async (customer: any) => {
        setIsLoading(true);
        try {
            const customerName = `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || customer.companyName || customer.phone || 'Customer';
            const tier = (customer.loyaltyTier || 'bronze') as 'bronze' | 'silver' | 'gold' | 'platinum';
            pos.setCustomer(customer.id, customerName, {
                id: customer.id,
                name: customerName,
                phone: customer.phone || '',
                loyaltyCardNumber: customer.loyaltyCardNumber || '',
                loyaltyPoints: parseFloat(customer.loyaltyPoints || '0'),
                loyaltyTier: tier,
                storeCredit: parseFloat(customer.storeCredit || '0'),
                tierDiscountPercent: 0,
            });
            saveToRecent(customer);
            if (customer.phone) {
                verifyCustomerInBackground(customer.phone, customer.firstName, customer.lastName);
            }
            setPhone('');
            setSearchResults([]);
            onClose();
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreate = async () => {
        if (!form.firstName || !form.lastName || !form.phone) return;
        setIsLoading(true);
        const result = await onCreateCustomer({
            ...form,
            dateOfBirth: form.dateOfBirth ? form.dateOfBirth : undefined,
        });
        setIsLoading(false);
        if (result) {
            saveToRecent(result);
            verifyCustomerInBackground(form.phone, form.firstName, form.lastName);
            setForm({ firstName: '', lastName: '', phone: '', dateOfBirth: '' });
            setMode('search');
            onClose();
        }
    };

    const handleWalkIn = () => {
        pos.setCustomer('walk-in', 'Walk-in Customer', {
            id: 'walk-in',
            name: 'Walk-in Customer',
            phone: '',
            loyaltyCardNumber: '',
            loyaltyPoints: 0,
            loyaltyTier: 'bronze' as const,
            storeCredit: 0,
            tierDiscountPercent: 0,
        });
        pos.setCustomerVerification(null);
        onClose();
    };

    const tierColors: Record<string, string> = {
        bronze: 'from-amber-700 to-amber-800',
        silver: 'from-slate-400 to-slate-500',
        gold: 'from-amber-400 to-yellow-500',
        platinum: 'from-purple-500 to-indigo-600',
    };

    const tierIcons: Record<string, React.ElementType> = {
        bronze: Star,
        silver: Star,
        gold: Award,
        platinum: Gift,
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-hidden flex flex-col"
                    >
                        {/* Header */}
                        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-5 text-white flex-shrink-0">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-xl bg-white/20">
                                        <Users size={20} />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-bold">
                                            {mode === 'search' ? 'Customer' : 'New Customer'}
                                        </h2>
                                        <p className="text-emerald-100 text-xs">
                                            {mode === 'search'
                                                ? 'Search, select recent, or walk-in'
                                                : 'Quick register a new customer'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="p-2 rounded-xl bg-white/20 hover:bg-white/30 transition-colors"
                                    type="button"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        </div>

                        {/* Current Customer Banner */}
                        {currentCustomer && (
                            <div className="px-6 py-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-100 flex-shrink-0">
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-sm font-bold text-emerald-800 flex items-center gap-1.5">
                                        <ShieldCheck size={14} />
                                        Attached Customer
                                    </p>
                                    <button
                                        onClick={onRemoveCustomer}
                                        className="text-xs text-rose-500 font-medium hover:text-rose-700 hover:bg-rose-50 px-2 py-1 rounded-lg transition-colors"
                                        type="button"
                                    >
                                        Remove
                                    </button>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-600 font-bold text-xl shadow-sm">
                                        {currentCustomer.name?.charAt(0)?.toUpperCase() || '?'}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-emerald-900">{currentCustomer.name}</p>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            {currentCustomer.phone && (
                                                <span className="text-xs text-emerald-600">{currentCustomer.phone}</span>
                                            )}
                                            {currentCustomer.loyaltyPoints > 0 && (
                                                <span className="text-xs text-amber-600 font-bold">
                                                    ⭐ {currentCustomer.loyaltyPoints?.toLocaleString()}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className={clsx(
                                        'px-3 py-1 rounded-full text-[10px] font-bold text-white bg-gradient-to-r shadow-sm',
                                        tierColors[currentCustomer.loyaltyTier] || tierColors.bronze
                                    )}>
                                        {currentCustomer.loyaltyTier?.toUpperCase()}
                                    </div>
                                </div>
                                {currentCustomer.storeCredit > 0 && (
                                    <div className="mt-2 flex items-center gap-1.5 text-xs text-blue-600 bg-blue-50 rounded-lg px-3 py-1.5">
                                        <CreditCard size={12} />
                                        <span>Store Credit: <strong>₦{currentCustomer.storeCredit?.toLocaleString()}</strong></span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Content */}
                        <div className="p-6 overflow-y-auto flex-1">
                            {mode === 'search' ? (
                                <div className="space-y-4">
                                    {/* Walk-in Button */}
                                    <motion.button
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.99 }}
                                        onClick={handleWalkIn}
                                        className="flex items-center justify-center gap-2 w-full py-3.5 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl font-semibold text-sm text-blue-700 hover:from-blue-100 hover:to-indigo-100 transition-all shadow-sm"
                                        type="button"
                                    >
                                        <Users size={18} />
                                        Walk-in Customer (Quick Checkout)
                                        <ArrowRight size={14} className="ml-1" />
                                    </motion.button>

                                    {/* Recent Customers */}
                                    {showRecent && recentCustomers.length > 0 && (
                                        <div>
                                            <div className="flex items-center gap-1.5 mb-2">
                                                <History size={12} className="text-slate-400" />
                                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                                    Recent Customers
                                                </p>
                                            </div>
                                            <div className="space-y-1">
                                                {recentCustomers.map((customer) => {
                                                    const TierIcon = tierIcons[customer.loyaltyTier] || Star;
                                                    return (
                                                        <motion.button
                                                            key={customer.id}
                                                            whileHover={{ scale: 1.01, x: 2 }}
                                                            whileTap={{ scale: 0.99 }}
                                                            onClick={() => handleSelectCustomer(customer)}
                                                            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-50 transition-all text-left group border border-transparent hover:border-slate-200"
                                                            type="button"
                                                        >
                                                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center text-slate-600 font-bold text-lg flex-shrink-0 shadow-sm">
                                                                {customer.firstName?.charAt(0)?.toUpperCase() || '?'}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-sm font-semibold text-slate-900">
                                                                    {customer.firstName} {customer.lastName}
                                                                </p>
                                                                <p className="text-xs text-slate-500">{customer.phone}</p>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                {customer.loyaltyPoints > 0 && (
                                                                    <span className="text-[10px] text-amber-600 font-bold">
                                                                        ⭐ {customer.loyaltyPoints}
                                                                    </span>
                                                                )}
                                                                {customer.loyaltyTier && (
                                                                    <TierIcon
                                                                        size={14}
                                                                        className={clsx(
                                                                            customer.loyaltyTier === 'platinum' ? 'text-purple-500' :
                                                                                customer.loyaltyTier === 'gold' ? 'text-amber-500' :
                                                                                    'text-slate-400'
                                                                        )}
                                                                    />
                                                                )}
                                                            </div>
                                                        </motion.button>
                                                    );
                                                })}
                                            </div>
                                            <div className="mt-2 pt-2 border-t border-slate-100">
                                                <p className="text-[10px] text-slate-400 text-center">OR SEARCH BELOW</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Search Input */}
                                    <div className="relative">
                                        <Search size={16} className="absolute left-4 top-4 text-slate-400" />
                                        <input
                                            ref={inputRef}
                                            type="text"
                                            placeholder="Type name or phone number..."
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            className="w-full pl-12 pr-10 py-3.5 border-2 border-slate-200 rounded-2xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 transition-all"
                                        />
                                        {isSearching && (
                                            <Loader2 size={16} className="absolute right-4 top-4 text-slate-400 animate-spin" />
                                        )}
                                        {phone && !isSearching && (
                                            <button
                                                onClick={() => setPhone('')}
                                                className="absolute right-4 top-4 p-1 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors"
                                                type="button"
                                            >
                                                <X size={16} />
                                            </button>
                                        )}
                                    </div>

                                    {/* Search Results */}
                                    <AnimatePresence>
                                        {searchResults.length > 0 && (
                                            <motion.div
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                exit={{ opacity: 0, height: 0 }}
                                                className="border border-slate-200 rounded-2xl overflow-hidden divide-y divide-slate-100 max-h-64 overflow-y-auto shadow-sm"
                                            >
                                                {searchResults.map((customer: any) => (
                                                    <motion.button
                                                        key={customer.id}
                                                        initial={{ opacity: 0, x: -10 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        onClick={() => handleSelectCustomer(customer)}
                                                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-emerald-50 transition-colors text-left"
                                                        type="button"
                                                    >
                                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center text-slate-600 font-bold text-lg flex-shrink-0 shadow-sm">
                                                            {(customer.firstName || customer.name)?.charAt(0)?.toUpperCase() || '?'}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-semibold text-slate-900">
                                                                {customer.firstName} {customer.lastName}
                                                            </p>
                                                            <div className="flex items-center gap-2 mt-0.5">
                                                                <p className="text-xs text-slate-500">{customer.phone}</p>
                                                                {customer.totalSpent > 0 && (
                                                                    <span className="text-[10px] text-slate-400">
                                                                        · ₦{parseFloat(customer.totalSpent).toLocaleString()}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {customer.loyaltyTier && (
                                                            <span className={clsx(
                                                                'px-2.5 py-1 rounded-full text-[10px] font-bold text-white bg-gradient-to-r shadow-sm flex-shrink-0',
                                                                tierColors[customer.loyaltyTier] || tierColors.bronze
                                                            )}>
                                                                {customer.loyaltyTier.toUpperCase()}
                                                            </span>
                                                        )}
                                                    </motion.button>
                                                ))}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>

                                    {/* No Results */}
                                    {phone.trim().length >= 2 && searchResults.length === 0 && !isSearching && (
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            className="text-center py-8 bg-slate-50 rounded-2xl border border-slate-100"
                                        >
                                            <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                                                <User size={24} className="text-slate-300" />
                                            </div>
                                            <p className="text-sm font-semibold text-slate-500">No customers found</p>
                                            <p className="text-xs text-slate-400 mt-1">
                                                "{phone}" didn't match any records
                                            </p>
                                            <button
                                                onClick={() => {
                                                    setForm({ ...form, phone: phone.trim() });
                                                    setMode('create');
                                                }}
                                                className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 bg-emerald-500 text-white rounded-xl text-sm font-bold hover:bg-emerald-600 transition-all shadow-sm"
                                                type="button"
                                            >
                                                <UserPlus size={14} />
                                                Create "{phone.trim()}"
                                            </button>
                                        </motion.div>
                                    )}

                                    {/* Create New Button */}
                                    <button
                                        onClick={() => setMode('create')}
                                        className="flex items-center justify-center gap-2 w-full py-3 border-2 border-dashed border-slate-300 rounded-2xl font-semibold text-sm text-slate-500 hover:border-emerald-300 hover:text-emerald-600 hover:bg-emerald-50 transition-all"
                                        type="button"
                                    >
                                        <UserPlus size={16} />
                                        Create New Customer
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-4 border border-emerald-100">
                                        <p className="text-xs text-emerald-700 font-medium">
                                            New customers get <strong>Bronze tier</strong> benefits automatically
                                        </p>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs font-bold text-slate-600 mb-1.5">
                                                First Name <span className="text-rose-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={form.firstName}
                                                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                                                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 transition-all"
                                                placeholder="John"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-600 mb-1.5">
                                                Last Name <span className="text-rose-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={form.lastName}
                                                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                                                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 transition-all"
                                                placeholder="Doe"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">
                                            Phone Number <span className="text-rose-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <Phone size={14} className="absolute left-4 top-4 text-slate-400" />
                                            <input
                                                type="tel"
                                                value={form.phone}
                                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                                className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 transition-all"
                                                placeholder="08012345678"
                                            />
                                        </div>
                                        <p className="text-[10px] text-slate-400 mt-1.5">
                                            Used for loyalty tracking and verification
                                        </p>
                                    </div>
                                    <div>
                                         <label className="block text-xs font-bold text-slate-600 mb-1.5">
                                             Date of Birth <span className="text-slate-400 font-normal">(Optional)</span>
                                         </label>
                                         <div className="relative">
                                             <Calendar size={14} className="absolute left-4 top-4 text-slate-400" />
                                             <input
                                                 type="date"
                                                 value={form.dateOfBirth}
                                                 onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })}
                                                 className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 transition-all text-slate-700"
                                             />
                                         </div>
                                         <p className="text-[10px] text-slate-400 mt-1.5">
                                             Used for birthday rewards & loyalty perks
                                         </p>
                                     </div>

                                    <div className="flex gap-3 pt-2">
                                        <button
                                            onClick={() => {
                                                setMode('search');
                                                setForm({ firstName: '', lastName: '', phone: '', dateOfBirth: '' });
                                            }}
                                            className="flex-1 py-3 border-2 border-slate-200 rounded-xl font-semibold text-sm text-slate-600 hover:bg-slate-50 transition-all"
                                            type="button"
                                        >
                                            ← Back
                                        </button>
                                        <button
                                            onClick={handleCreate}
                                            disabled={!form.firstName || !form.lastName || !form.phone || isLoading}
                                            className="flex-1 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-bold text-sm shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 hover:from-emerald-700 hover:to-teal-700"
                                            type="button"
                                        >
                                            {isLoading ? (
                                                <><Loader2 size={16} className="animate-spin" /> Creating...</>
                                            ) : (
                                                <><UserPlus size={16} /> Create & Attach</>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}